import { apiFetch, API_BASE_URL, tokenRef } from "./api";
import type { Company, CompanyWithPages, ListCompanyParams } from "./company.service";

export interface ClientHeartbeat {
  online: boolean;
  status: string;
  message: string;
  erp?: string;
  version?: string;
  migration_state?: string;
  migration_version?: number;
  migration_error?: string;
  last_seen_at: string;
}

export interface Client {
  id: string;
  active: boolean;
  name: string;
  group_name: string;
  email: string | null;
  system_id: string;
  system_url?: string | null;
  splash_url?: string | null;
  splash_url_dark?: string | null;
  params?: Record<string, unknown> | null;
  heartbeat?: ClientHeartbeat | null;
}

export interface ClientModule {
  id: string;
  client_id: string;
  module_id: string;
}

export interface CreateClientModuleRequest {
  client_id: string;
  module_id: string;
}


export const mockedClientData: Client[] = [
  {
    id: "1",
    active: true,
    name: "Teste",
    group_name: "Grupo teste",
    email: "email@teste.com",
    system_id: "C5",
    system_url: "https://consinco.mambo.com.br"
  },
];

export type ListClientParams = {
  offset: number;
  limit: number;
  active?: boolean;
  name?: string;
  groupName?: string;
  email?: string;
  system?: string;
}

export type ClientWithPages = {
  total: number,
  clients: Client[]
}

export type ListClientCompaniesParams = {
  offset: number;
  limit: number;
  clientId: string;
}

export type ClientTheme = {
  id: string;
  client_id: string;
  theme: string;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
}

export type CreateClientThemeRequest = {
  client_id: string;
  theme: string;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
}

export function getClients(data: ListClientParams) {
  const params = new URLSearchParams();

  params.set('offset', String(data.offset));
  params.set('limit', String(data.limit));
  if (data.active !== undefined)
    params.set('active', String(data.active));
  if (data.email !== undefined)
    params.set('email', data.email);
  if (data.groupName !== undefined)
    params.set('group_name', data.groupName);
  if (data.system !== undefined)
    params.set('system', data.system);
  if (data.name !== undefined)
    params.set('name', data.name);

  const paramString = params.toString();
  return apiFetch<ClientWithPages>(`/api/clients${paramString ? "?" + paramString : ""}`);
}

export function getClient(identifier: string) {
  return apiFetch<Client>(`/api/clients/${identifier}`);
}

export type CreateClientResponse = {
  client: Client;
  enrollment_token?: string;
}

export function createClient(data: Client) {
  return apiFetch<CreateClientResponse>("/api/clients", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function generateEnrollmentToken(clientId: string) {
  return apiFetch<{ token: string }>(`/api/clients/${clientId}/enrollment-token`, {
    method: "POST",
  });
}

export function updateClient(id: string, data: Partial<Client>) {
  return apiFetch<Client>(`/api/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteClient(id: string) {
  return apiFetch<void>(`/api/clients/${id}`, {
    method: "DELETE",
  });
}

export function getCompaniesByClient(data: ListCompanyParams) {
  const params = new URLSearchParams();

  params.set('offset', String(data.offset));
  params.set('limit', String(data.limit));
  if (data.clientId)
    params.set('client_id', String(data.clientId));
  if (data.cnpj)
    params.set('cnpj', String(data.cnpj));
  if (data.erpCode)
    params.set('erp_code', String(data.erpCode));
  if (data.name)
    params.set('name', String(data.name));
  if (data.socialReason)
    params.set('social_reason', String(data.socialReason));

  const paramString = params.toString();
  return apiFetch<CompanyWithPages>(`/api/clients/${data.clientId}/companies${paramString ? "?" + paramString : ""}`);
}

export function getModulesByClient(clientId: string) {
  return apiFetch<ClientModule[]>(`/api/clients/${clientId}/modules`);
}

export interface TenantSchedule {
  name: string;
  cron: string;
  command: string;
  timeout_seconds: number;
  next_run: string; // ISO 8601
  last_run: string; // ISO 8601, zero-time if never run
  last_outcome: "success" | "failed" | "timed_out" | "";
}

export function getTenantSchedules(clientId: string) {
  return apiFetch<TenantSchedule[]>(`/api/clients/${clientId}/schedules`);
}

export function addModuleToClient(data: CreateClientModuleRequest) {
  return apiFetch<ClientModule>(`/api/clients/${data.client_id}/modules`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function removeModuleFromClient(clientId: string, moduleId: string) {
  return apiFetch<ClientModule>(`/api/clients/${clientId}/modules/${moduleId}`, {
    method: "DELETE",
  });
}

export function getClientThemes(clientId: string) {
  return apiFetch<ClientTheme[]>(`/api/clients/${clientId}/theme`);
}

export function createClientTheme(theme: CreateClientThemeRequest) {
  return apiFetch<ClientTheme>(`/api/clients/${theme.client_id}/theme`, {
    method: "POST",
    body: JSON.stringify(theme)
  });
}

export function updateClientTheme(theme: Partial<ClientTheme>) {
  return apiFetch<ClientTheme>(`/api/clients/${theme.client_id}/theme/${theme.id}`, {
    method: "PATCH",
    body: JSON.stringify(theme)
  });
}

export async function uploadClientSplash(clientId: string, file: File, theme: "light" | "dark" = "light"): Promise<Client> {
  const token = tokenRef.current;
  const form = new FormData();
  form.append("splash", file);
  const endpoint = theme === "dark" ? "splash-dark" : "splash";
  const res = await fetch(`${API_BASE_URL}/api/clients/${clientId}/${endpoint}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ message: "upload failed" }));
    throw new Error(e.message);
  }
  return res.json();
}